import React, { useEffect, useState } from "react";
import { Star, ShieldCheck, Quote, Edit3, X, MessageCircle } from "lucide-react";
import { useI18n } from "@/i18n";
import { fetchReviews, submitReview } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Google Business review link — update once Google Business Profile is live.
// For now points to a Maps search which lets users find & review the business.
const GOOGLE_REVIEW_URL =
  "https://www.google.com/maps/search/?api=1&query=Gallura+Drive+Olbia";
const WHATSAPP_NUMBER = "393470011246";

const AVATAR_COLORS = [
  "bg-[#0A2463] text-white",
  "bg-[#C9A14A] text-[#0A2463]",
  "bg-slate-700 text-white",
  "bg-emerald-700 text-white",
  "bg-rose-700 text-white",
  "bg-indigo-700 text-white",
];

const COPY = {
  it: {
    eyebrow: "Dicono di noi",
    title: "Recensioni dei nostri clienti",
    lead: "Puntualità, comfort e discrezione. Ecco cosa raccontano gli ospiti che hanno viaggiato con Gallura Drive.",
    aggregate: (avg, count) =>
      `${avg.toFixed(1)} / 5 su ${count} recensioni verificate`,
    verified: "Verificata",
    empty: "Nessuna recensione pubblicata ancora.",
    // Submission CTAs
    ctaWriteBlock: "Hai viaggiato con noi? Lascia una recensione",
    ctaWriteDesc: "Il tuo parere ci aiuta a migliorare e aiuta altri viaggiatori.",
    ctaForm: "Scrivi sul sito",
    ctaWhatsapp: "Via WhatsApp",
    ctaGoogle: "Su Google",
    // Modal
    modalTitle: "Lascia la tua recensione",
    modalDesc: "La tua recensione sarà pubblicata dopo una breve verifica.",
    fAuthor: "Il tuo nome",
    fEmail: "Email (facoltativa, non pubblicata)",
    fRating: "Il tuo voto",
    fService: "Servizio utilizzato (facoltativo)",
    fText: "La tua esperienza",
    fTextPlaceholder: "Puntualissimo, auto spaziosa, autista cortese...",
    submit: "Invia recensione",
    cancel: "Annulla",
    submitting: "Invio in corso…",
    successToast: "Grazie! La tua recensione è stata inviata ed è in moderazione.",
    errorToast: "Qualcosa è andato storto. Riprova o scrivici su WhatsApp.",
    minTextError: "Il testo deve avere almeno 10 caratteri",
    nameError: "Inserisci il tuo nome (almeno 2 caratteri)",
    whatsappPrefill:
      "Ciao Fabrizio! Vorrei lasciarti una recensione:\n\n⭐ Voto: 5/5\n🚗 Servizio: [transfer aeroporto / tour / altro]\n📝 La mia esperienza: ",
  },
  en: {
    eyebrow: "What they say",
    title: "Reviews from our customers",
    lead: "Punctuality, comfort and discretion. Here's what guests who travelled with Gallura Drive have to say.",
    aggregate: (avg, count) => `${avg.toFixed(1)} / 5 on ${count} verified reviews`,
    verified: "Verified",
    empty: "No reviews published yet.",
    ctaWriteBlock: "Travelled with us? Leave a review",
    ctaWriteDesc: "Your feedback helps us improve and helps other travellers.",
    ctaForm: "Write on the site",
    ctaWhatsapp: "Via WhatsApp",
    ctaGoogle: "On Google",
    modalTitle: "Leave your review",
    modalDesc: "Your review will be published after a short verification.",
    fAuthor: "Your name",
    fEmail: "Email (optional, not published)",
    fRating: "Your rating",
    fService: "Service used (optional)",
    fText: "Your experience",
    fTextPlaceholder: "On time, spacious car, friendly driver...",
    submit: "Send review",
    cancel: "Cancel",
    submitting: "Sending…",
    successToast: "Thank you! Your review was sent and is in moderation.",
    errorToast: "Something went wrong. Please retry or message us on WhatsApp.",
    minTextError: "Text must be at least 10 characters",
    nameError: "Enter your name (at least 2 characters)",
    whatsappPrefill:
      "Hi Fabrizio! I'd like to leave a review:\n\n⭐ Rating: 5/5\n🚗 Service: [airport transfer / tour / other]\n📝 My experience: ",
  },
};

const initial = (name) => (name ? name.trim().charAt(0).toUpperCase() : "?");
const formatDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export default function ReviewsSection() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.it;
  const [reviews, setReviews] = useState([]);
  const [aggregate, setAggregate] = useState({ count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const emptyDraft = { author: "", email: "", rating: 5, text: "", service_type: "" };
  const [draft, setDraft] = useState(emptyDraft);

  const handleSubmit = async () => {
    if (!draft.author || draft.author.trim().length < 2) {
      toast.error(c.nameError);
      return;
    }
    if (!draft.text || draft.text.trim().length < 10) {
      toast.error(c.minTextError);
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        author: draft.author.trim(),
        rating: draft.rating,
        text: draft.text.trim(),
        language: lang,
      };
      if (draft.email?.trim()) body.email = draft.email.trim();
      if (draft.service_type?.trim()) body.service_type = draft.service_type.trim();
      await submitReview(body);
      toast.success(c.successToast);
      setShowForm(false);
      setDraft(emptyDraft);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : c.errorToast);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchReviews()
      .then((d) => {
        setReviews(d.reviews || []);
        setAggregate(d.aggregate || { count: 0, average: 0 });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // fetchReviews is a stable module import; mount-only intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inject AggregateRating JSON-LD for Google rich snippets
  useEffect(() => {
    if (!aggregate.count) return;
    const id = "ld-aggregate-rating";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Gallura Drive",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: aggregate.average,
        reviewCount: aggregate.count,
        bestRating: 5,
        worstRating: 1,
      },
      review: reviews.slice(0, 10).map((r) => ({
        "@type": "Review",
        reviewRating: {
          "@type": "Rating",
          ratingValue: r.rating,
          bestRating: 5,
        },
        author: { "@type": "Person", name: r.author },
        reviewBody: r.text,
        datePublished: r.created_at,
      })),
    });
    return () => {
      const n = document.getElementById(id);
      if (n) n.remove();
    };
  }, [reviews, aggregate]);

  if (loading) return null; // avoid layout flash before data loads

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="bg-white py-20 lg:py-28 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="uppercase tracking-[0.3em] text-xs text-slate-500 mb-4">
            {c.eyebrow}
          </div>
          <h2 className="font-display text-4xl md:text-5xl brand-text leading-tight">
            {c.title}
          </h2>
          <p className="mt-6 text-slate-700 leading-relaxed text-base md:text-lg">
            {c.lead}
          </p>

          {aggregate.count > 0 && (
            <div
              data-testid="reviews-aggregate"
              className="mt-8 inline-flex items-center gap-3 bg-[#F9F7F3] rounded-full pl-3 pr-5 py-2 border border-slate-200"
            >
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={`agg-${n}`}
                    className={`w-5 h-5 ${
                      n <= Math.round(aggregate.average)
                        ? "accent-text fill-current"
                        : "text-slate-300"
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-slate-700 font-medium">
                {c.aggregate(aggregate.average, aggregate.count)}
              </span>
            </div>
          )}
        </div>

        {/* Reviews grid */}
        {reviews.length === 0 ? (
          <div className="mt-14 text-slate-500 text-center py-10">{c.empty}</div>
        ) : (
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <article
                key={r.id}
                data-testid={`review-card-${r.id}`}
                className="relative bg-[#F9F7F3] rounded-2xl p-7 border border-slate-200 hover:border-slate-400 transition-colors flex flex-col"
              >
                <Quote className="absolute top-5 right-5 w-7 h-7 text-slate-300" />

                {/* Author */}
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className={`w-11 h-11 rounded-full grid place-items-center font-display text-lg ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}
                    aria-hidden="true"
                  >
                    {initial(r.author)}
                  </div>
                  <div>
                    <div className="font-display text-base brand-text leading-tight">
                      {r.author}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {formatDate(r.created_at, lang)}
                      {r.service_type ? ` · ${r.service_type}` : ""}
                    </div>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-0.5 mb-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={`${r.id}-star-${n}`}
                      className={`w-4 h-4 ${
                        n <= r.rating ? "accent-text fill-current" : "text-slate-300"
                      }`}
                    />
                  ))}
                </div>

                {/* Text */}
                <p className="text-slate-700 leading-relaxed text-sm flex-1">
                  "{r.text}"
                </p>

                {/* Verified badge */}
                {r.verified && (
                  <div className="mt-5 inline-flex items-center gap-1.5 text-xs text-emerald-700 font-medium">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {c.verified}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        {/* CTA block — write a review */}
        <div
          data-testid="write-review-cta"
          className="mt-16 bg-[#F9F7F3] rounded-2xl p-8 md:p-10 border border-slate-200 grid md:grid-cols-[1fr_auto] items-center gap-6"
        >
          <div>
            <div className="font-display text-xl md:text-2xl brand-text">
              {c.ctaWriteBlock}
            </div>
            <p className="text-sm md:text-base text-slate-600 mt-2">
              {c.ctaWriteDesc}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-start md:justify-end">
            <Button
              data-testid="cta-write-form"
              onClick={() => setShowForm(true)}
              className="brand-bg text-white rounded-full px-5"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              {c.ctaForm}
            </Button>
            <a
              data-testid="cta-write-whatsapp"
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(c.whatsappPrefill)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-5 h-10 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 transition-colors text-sm font-medium"
            >
              <MessageCircle className="w-4 h-4" />
              {c.ctaWhatsapp}
            </a>
            <a
              data-testid="cta-write-google"
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-5 h-10 border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
              </svg>
              {c.ctaGoogle}
            </a>
          </div>
        </div>
      </div>

      {/* Submission Modal */}
      {showForm && (
        <div
          data-testid="review-form-modal"
          className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4"
          onClick={() => !submitting && setShowForm(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 md:p-8 w-full max-w-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-2xl brand-text">{c.modalTitle}</h3>
                <p className="text-sm text-slate-500 mt-1">{c.modalDesc}</p>
              </div>
              <button
                data-testid="review-form-close"
                onClick={() => !submitting && setShowForm(false)}
                className="text-slate-400 hover:text-slate-700 p-1"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label>{c.fAuthor} *</Label>
                <Input
                  data-testid="review-form-author"
                  value={draft.author}
                  onChange={(e) => setDraft({ ...draft, author: e.target.value })}
                  placeholder="Marco R."
                  maxLength={120}
                />
              </div>

              <div>
                <Label>{c.fEmail}</Label>
                <Input
                  data-testid="review-form-email"
                  type="email"
                  value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                  placeholder="marco@example.com"
                />
              </div>

              <div>
                <Label>{c.fRating} *</Label>
                <div className="flex gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={`form-star-${n}`}
                      type="button"
                      data-testid={`review-form-star-${n}`}
                      onClick={() => setDraft({ ...draft, rating: n })}
                      className="transition-transform hover:scale-110"
                      aria-label={`${n} stars`}
                    >
                      <Star
                        className={`w-8 h-8 ${
                          n <= draft.rating
                            ? "accent-text fill-current"
                            : "text-slate-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>{c.fService}</Label>
                <Input
                  data-testid="review-form-service"
                  value={draft.service_type}
                  onChange={(e) => setDraft({ ...draft, service_type: e.target.value })}
                  placeholder="Transfer aeroporto Olbia → Porto Cervo"
                  maxLength={120}
                />
              </div>

              <div>
                <Label>{c.fText} *</Label>
                <textarea
                  data-testid="review-form-text"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 min-h-[120px] font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2463]"
                  value={draft.text}
                  onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                  placeholder={c.fTextPlaceholder}
                  maxLength={1000}
                />
                <div className="text-xs text-slate-400 text-right">
                  {draft.text.length}/1000
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-2 border-t border-slate-200">
                <Button
                  variant="outline"
                  data-testid="review-form-cancel"
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                >
                  {c.cancel}
                </Button>
                <Button
                  data-testid="review-form-submit"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="brand-bg text-white"
                >
                  {submitting ? c.submitting : c.submit}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
